import { css, cx } from '@linaria/core';
import { baseTheme, flex } from 'styles/utils';
import { Button } from 'components/Button';
import { IconCheckCircleV2, IconRunner, IconGift } from 'components/Icons';
import { computeDocumentation } from 'modules/Compute/utils';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { useSubscriptionPlan } from 'modules/User/components/Settings/Billing/hooks/useSubscriptionPlan';
import { COMMUNITY_PLAN_ID, ENTERPRISE_PLAN_ID, ESSENTIAL_PLAN_ID, INTERNAL_PLAN_ID, PROFESSIONAL_PLAN_ID } from 'modules/Organizations/constants';
import { useRouter } from 'next/router';
import { useIsLoggedIn } from 'modules/Auth/hooks';
import { useAuthActions } from 'modules/Auth/AuthContext';
import { AuthModalState } from 'modules/Auth/constant';
import { HomeCard } from '../components/HomeCard';
import { UpgradePlanWrapper } from '../components/RestrictBillingPlan';

// TODO: Refactor this to be more reusable?

const BILLING_PLAN_DESCRIPTIONS: Record<string, string> = {
  [ESSENTIAL_PLAN_ID]: 'Core AI development capabilities across our cloud or your self-hosted compute.',
  [PROFESSIONAL_PLAN_ID]: 'Development and production AI workloads, more advanced dedicated GPUs and Control Center.',
  [ENTERPRISE_PLAN_ID]: 'Unlimited SaaS or VPC AI development and production workloads.',
  [INTERNAL_PLAN_ID]: 'Unlimited SaaS or VPC AI development and production workloads.'
};
export function RunnersPromo() {
  const {
    track
  } = useDataCollection();
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();
  const {
    openLoginModal
  } = useAuthActions();
  const {
    enableLocalRunnersLDFF
  } = useFeatureFlagsLD();
  const {
    subscription
  } = useSubscriptionPlan();
  const userOnCommunityPlan = subscription?.plan?.id === COMMUNITY_PLAN_ID;
  const isLocalRunnersEnabledOnPlan = Boolean(subscription?.plan?.id) && !userOnCommunityPlan;
  const handleConnectRunnerClick = () => {
    track('Home Page -> Local Runners -> Connect a Runner Clicked');
    if (!isLoggedIn) {
      openLoginModal(AuthModalState.SHOW_SIGN_UP);
      return;
    }
    router.push(computeDocumentation.localRunnersQuickstart);
  };
  if (!enableLocalRunnersLDFF) {
    return null;
  }
  return <HomeCard>
      <section className={runnerStyles.container}>
        {/* LEFT SIDE */}
        <div className={runnerStyles.leftSide}>
          <div className={runnerStyles.leftSideHeader}>
            <div className={runnerStyles.icon}>
              <IconRunner size={24} color={baseTheme.light.colors.white} />
            </div>
            <div className={runnerStyles.newBadge}>NEW</div>
          </div>
          <div className={runnerStyles.leftSideContent}>
            <div className={cx(flex.init, flex.direction.column, flex.gap.half)}>
              <p className={runnerStyles.leftSideTitle}>Local Runners</p>
              <p className={runnerStyles.leftSideText}>
                Run powerful AI models directly on your machine. No internet required, complete privacy, lightning-fast inference. Your data never
                leaves your computer.
              </p>
            </div>
            <div className={cx(flex.init, flex.direction.column, flex.gap[1])} style={{
            marginTop: '1.5rem'
          }}>
              {!isLocalRunnersEnabledOnPlan && <p style={{
              fontWeight: 500
            }}>Try it free for 2 hours/month — no upgrade required</p>}
              <Button onClick={handleConnectRunnerClick} size="md" className={runnerStyles.cta}>
                {isLocalRunnersEnabledOnPlan ? 'Connect Runner' : 'Connect a Runner for free'}
              </Button>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        {isLocalRunnersEnabledOnPlan && subscription?.plan ? <PlanIncludeLocalRunnersRightSide plan={subscription.plan} /> : <PlanUpgradeRightSide />}
      </section>
    </HomeCard>;
}
function PlanUpgradeRightSide() {
  return <div className={runnerStyles.rightSide}>
      <div className={runnerStyles.rightSideHeader}>
        <div className={runnerStyles.rightSideHeaderRail}>
          <div className={runnerStyles.promoBadge}>
            <IconGift size={12} color={baseTheme.light.colors.success700} /> Founder Promotion
          </div>
          <div className={runnerStyles.price}>$1/month</div>
        </div>
        <div className={runnerStyles.rightSideHeaderContent}>
          <p className={runnerStyles.rightSideTitle}>Upgrade to Developer Plan</p>
          <p className={runnerStyles.rightSideText}>
            Built for developers and ML Engineers looking to build with speed and security, while controlling their costs.
          </p>
        </div>
      </div>
      <div className={runnerStyles.rightSideContent}>
        <div className={runnerStyles.rightSideList}>
          <p style={{
          fontWeight: 500,
          marginBottom: '0.5rem'
        }}>Everything in Community, plus: </p>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.35rem'
        }}>
            <span className={runnerStyles.rightSideListItem}>
              <IconCheckCircleV2 size={16} color={baseTheme.light.colors.success500} />
              <span className={runnerStyles.rightSideText}>Connect up to 5 Local Runners</span>
            </span>
            <span className={runnerStyles.rightSideListItem}>
              <IconCheckCircleV2 size={16} color={baseTheme.light.colors.success500} />
              <span className={runnerStyles.rightSideText}>Unlimited Runner Hours</span>
            </span>
            <span className={runnerStyles.rightSideListItem}>
              <IconCheckCircleV2 size={16} color={baseTheme.light.colors.success500} />
              <span className={runnerStyles.rightSideText}>Priority support & updates</span>
            </span>
          </div>
        </div>
      </div>
      <div className={runnerStyles.rightSideFooter}>
        <UpgradePlanWrapper redirectToBillingPage requiredFeaturedFlags={['ComputeOrchestration', 'ComputeResourceLocal']} requiredScopes={[permissionMatchers.runners.list, permissionMatchers.runners.add]} upgradeCtaLabel="Upgrade to Developer Plan" upgradeCtaTrackEvent="Home Page -> Local Runners -> Upgrade to Developer Plan Clicked" />
        <p className={runnerStyles.disclaimer}>This promotional pricing is available as a founder offer for our first cohort of creators.</p>
      </div>
    </div>;
}
function PlanIncludeLocalRunnersRightSide({
  plan
}: {
  plan: CF.API.Users.SubscriptionPlan;
}) {
  const isEnterpriseOrInternalPlan = [ENTERPRISE_PLAN_ID, INTERNAL_PLAN_ID].includes(plan.id);
  const maximumRunnersText = isEnterpriseOrInternalPlan ? 'Connect unlimited Local Runners' : `Connect up to 5 Local Runners`;
  return <div className={runnerStyles.rightSide}>
      <div className={runnerStyles.rightSideHeader}>
        <div className={runnerStyles.rightSideHeaderContent} style={{
        maxWidth: '100%'
      }}>
          <p className={runnerStyles.rightSideTitle}>{plan.name}</p>
          <p className={runnerStyles.rightSideText}>{BILLING_PLAN_DESCRIPTIONS[plan.id]}</p>
        </div>
      </div>
      <div className={runnerStyles.rightSideContent}>
        <div className={runnerStyles.rightSideList}>
          <p style={{
          fontWeight: 500,
          marginBottom: '0.5rem'
        }}>Included in your plan </p>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.35rem'
        }}>
            <span className={runnerStyles.rightSideListItem}>
              <IconCheckCircleV2 size={16} color={baseTheme.light.colors.success500} />
              <span className={runnerStyles.rightSideText}>{maximumRunnersText}</span>
            </span>
            <span className={runnerStyles.rightSideListItem}>
              <IconCheckCircleV2 size={16} color={baseTheme.light.colors.success500} />
              <span className={runnerStyles.rightSideText}>Unlimited Runner Hours</span>
            </span>
            <span className={runnerStyles.rightSideListItem}>
              <IconCheckCircleV2 size={16} color={baseTheme.light.colors.success500} />
              <span className={runnerStyles.rightSideText}>Priority support & updates</span>
            </span>
          </div>
        </div>
      </div>
    </div>;
}
const runnerStyles = {
  container: "odules__Home__cards__RunnersPromo_tsx-container-cufi8k7",
  leftSide: "odules__Home__cards__RunnersPromo_tsx-leftSide-l1rl6p82",
  rightSide: "odules__Home__cards__RunnersPromo_tsx-rightSide-ryb96qq",
  leftSideHeader: "odules__Home__cards__RunnersPromo_tsx-leftSideHeader-lq1prd",
  leftSideContent: "odules__Home__cards__RunnersPromo_tsx-leftSideContent-l1i0pnem",
  icon: "odules__Home__cards__RunnersPromo_tsx-icon-ii6bwg4",
  leftSideTitle: "odules__Home__cards__RunnersPromo_tsx-leftSideTitle-llwvmy8",
  leftSideText: "odules__Home__cards__RunnersPromo_tsx-leftSideText-l1rvy7aw",
  rightSideTitle: "odules__Home__cards__RunnersPromo_tsx-rightSideTitle-rjmbacs",
  rightSideText: "odules__Home__cards__RunnersPromo_tsx-rightSideText-rk2f144",
  rightSideFooter: "odules__Home__cards__RunnersPromo_tsx-rightSideFooter-rrsf5n7",
  rightSideList: "odules__Home__cards__RunnersPromo_tsx-rightSideList-rouw2pv",
  rightSideContent: "odules__Home__cards__RunnersPromo_tsx-rightSideContent-rod6tng",
  rightSideListItem: "odules__Home__cards__RunnersPromo_tsx-rightSideListItem-r1mdljsb",
  rightSideHeader: "odules__Home__cards__RunnersPromo_tsx-rightSideHeader-r1yy7o03",
  rightSideHeaderRail: "odules__Home__cards__RunnersPromo_tsx-rightSideHeaderRail-rxzxa5t",
  rightSideHeaderContent: "odules__Home__cards__RunnersPromo_tsx-rightSideHeaderContent-r19jamk6",
  newBadge: "odules__Home__cards__RunnersPromo_tsx-newBadge-nam35vt",
  promoBadge: "odules__Home__cards__RunnersPromo_tsx-promoBadge-p1fuumln",
  price: "odules__Home__cards__RunnersPromo_tsx-price-p1ow0wcy",
  disclaimer: "odules__Home__cards__RunnersPromo_tsx-disclaimer-d59r928",
  cta: "odules__Home__cards__RunnersPromo_tsx-cta-c1rt89n3"
};

require("./RunnersPromo.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./RunnersPromo.tsx");